import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useExam } from "../context/ExamContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Calculator, Brain, Landmark, MapPin, Newspaper,
  ListChecks, RefreshCw, CheckCircle2, Circle, Flame, Target,
  Lock, ChevronDown, CalendarDays, Loader2, Trophy, Sparkles,
} from "lucide-react";
import { getApiBaseUrl } from "../services/api";

const API_URL = getApiBaseUrl();

// Map each timetable column to an icon, colour and (where content exists) a
// one-tap deep-link into an engine already built in CLARIAS.
const taskAction = (key, text) => {
  const t = (text || "").toLowerCase();
  const hasMock = /mock|sectional|full-length|test/.test(t);
  switch (key) {
    case "maths":
      return { icon: Calculator, tone: "indigo", label: "Maths (Arithmetic)", to: "/csat-suite?mode=universal-practice", cta: "Practice" };
    case "reasoning":
      return { icon: Brain, tone: "violet", label: "Reasoning / Mental Ability", to: "/csat-suite?mode=universal-practice", cta: "Practice" };
    case "gs_national":
      return { icon: Landmark, tone: "amber", label: "GS — National", to: "/prelims/master", cta: "Study" };
    case "gs_state":
      return { icon: MapPin, tone: "rose", label: "GS — State", to: null, cta: null, soon: true };
    case "current_affairs":
      return { icon: Newspaper, tone: "sky", label: "Current Affairs", to: "/daily-intelligence-v2", cta: "Read CA" };
    case "daily_mcq":
      return { icon: ListChecks, tone: "emerald", label: "Daily MCQ Practice", to: "/csat-suite?mode=universal-practice", cta: "MCQ Set" };
    case "revision":
      return {
        icon: RefreshCw, tone: "teal", label: "Revision",
        to: hasMock ? "/csat-suite?mode=si-test-series" : null,
        cta: hasMock ? "Take Test" : null,
      };
    default:
      return { icon: Circle, tone: "slate", label: key, to: null, cta: null };
  }
};

const TONE = {
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  violet: "bg-violet-50 text-violet-700 border-violet-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  sky: "bg-sky-50 text-sky-700 border-sky-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  teal: "bg-teal-50 text-teal-700 border-teal-200",
  slate: "bg-slate-50 text-slate-600 border-slate-200",
};

const TASK_KEYS = ["maths", "reasoning", "gs_national", "gs_state", "current_affairs", "daily_mcq", "revision"];

const ProgressRing = ({ pct }) => {
  const r = 26, c = 2 * Math.PI * r;
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" className="flex-shrink-0">
      <circle cx="32" cy="32" r={r} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="6" />
      <circle
        cx="32" cy="32" r={r} fill="none" stroke="#fff" strokeWidth="6" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c - (c * pct) / 100}
        transform="rotate(-90 32 32)" style={{ transition: "stroke-dashoffset .6s ease" }}
      />
      <text x="32" y="37" textAnchor="middle" className="fill-white font-bold" fontSize="15">{pct}%</text>
    </svg>
  );
};

const DayCard = ({ day, dayData, done, onToggle, isToday, entitled }) => {
  const [open, setOpen] = useState(isToday);
  return (
    <div
      className={`rounded-xl border bg-white overflow-hidden transition-shadow hover:shadow-sm ${
        isToday ? "border-teal-400 ring-2 ring-teal-200" : done ? "border-emerald-200" : "border-slate-200"
      }`}
      data-testid={`roadmap-day-${day}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
        data-testid={`roadmap-day-toggle-${day}`}
      >
        <span
          className={`flex-shrink-0 flex h-9 w-9 items-center justify-center rounded-lg text-sm font-bold ${
            done ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-600"
          }`}
        >
          {day}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-800">Day {day}</span>
            {isToday && <Badge className="bg-teal-600 text-white border-0 text-[10px]">Today</Badge>}
            {done && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">Done</Badge>}
          </div>
          <p className="text-xs text-slate-500 truncate">
            {dayData.maths} · {dayData.reasoning}
          </p>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-100 space-y-2">
          {TASK_KEYS.map((key) => {
            const val = dayData[key];
            if (!val || val === "-") return null;
            const a = taskAction(key, val);
            const Icon = a.icon;
            return (
              <div key={key} className="flex items-start gap-3 rounded-lg border border-slate-100 p-2.5" data-testid={`roadmap-task-${day}-${key}`}>
                <span className={`flex-shrink-0 flex h-7 w-7 items-center justify-center rounded-md border ${TONE[a.tone]}`}>
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{a.label}</p>
                  <p className="text-sm text-slate-700 leading-snug">{val}</p>
                </div>
                {a.to ? (
                  <Button asChild size="sm" variant="outline" className="h-7 text-xs flex-shrink-0">
                    <a href={a.to} data-testid={`roadmap-cta-${day}-${key}`}>{a.cta}</a>
                  </Button>
                ) : a.soon ? (
                  <span className="text-[10px] text-slate-400 flex-shrink-0 mt-1">soon</span>
                ) : null}
              </div>
            );
          })}

          <Button
            onClick={() => onToggle(day)}
            disabled={!entitled}
            className={`w-full mt-1 ${done ? "bg-slate-100 text-slate-600 hover:bg-slate-200" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}
            data-testid={`roadmap-complete-${day}`}
          >
            {done ? <><Circle className="w-4 h-4 mr-2" /> Mark as not done</> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Mark day complete</>}
          </Button>
        </div>
      )}
    </div>
  );
};

const SIRoadmapPage = () => {
  const { user, token } = useAuth();
  const { isSI } = useExam();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [me, setMe] = useState(null);
  const [state, setState] = useState("TS");
  const [openPhase, setOpenPhase] = useState(0);

  const entitled = user?.role === "ADMIN" || user?.is_admin || (user?.pro_exams || []).includes("SI");
  const completed = useMemo(() => new Set(me?.completed_days || []), [me]);

  const load = useCallback(async (st) => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [meRes, planRes] = await Promise.all([
        fetch(`${API_URL}/api/si-roadmap/me`, { headers }),
        fetch(`${API_URL}/api/si-roadmap/plan?state=${st}`, { headers }),
      ]);
      const meData = await meRes.json();
      const planData = await planRes.json();
      setMe(meData);
      setPlan(planData);
    } catch (e) {
      toast.error("Could not load the roadmap");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!user || !token) { navigate("/auth"); return; }
    // initial: read my saved state, then load that variant
    (async () => {
      try {
        const meData = await (await fetch(`${API_URL}/api/si-roadmap/me`, { headers: { Authorization: `Bearer ${token}` } })).json();
        const st = meData?.state || "TS";
        setState(st);
        setMe(meData);
        await load(st);
      } catch {
        await load("TS");
      }
    })();
  }, [user, token]); // eslint-disable-line

  const switchState = async (st) => {
    if (st === state) return;
    setState(st);
    await load(st);
    if (entitled) {
      try {
        await fetch(`${API_URL}/api/si-roadmap/state`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ state: st }),
        });
      } catch { /* non-critical */ }
    }
  };

  const toggleDay = async (day) => {
    if (!entitled) return;
    try {
      const res = await fetch(`${API_URL}/api/si-roadmap/toggle-day`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ day }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error();
      setMe((prev) => ({
        ...prev,
        completed_days: data.completed_days,
        streak: data.streak,
        completed_count: data.completed_count,
        start_date: prev?.start_date || new Date().toISOString().slice(0, 10),
      }));
      if (data.completed) toast.success(`Day ${day} complete! 🎯`);
    } catch {
      toast.error("Could not update. Try again.");
    }
  };

  // Current day = days since start_date (capped 1..180); default day 1.
  const todayDay = useMemo(() => {
    if (!me?.start_date) return 1;
    const start = new Date(me.start_date + "T00:00:00");
    const diff = Math.floor((Date.now() - start.getTime()) / 86400000) + 1;
    return Math.max(1, Math.min(180, diff));
  }, [me]);

  const days = plan?.days || [];
  const phases = plan?.phases || [];
  const totalDone = me?.completed_count || 0;
  const pct = Math.round((totalDone / 180) * 100);
  const todayData = days.find((d) => d.day === todayDay);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (!isSI) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <Card className="border-teal-200">
          <CardContent className="p-8">
            <MapPin className="w-10 h-10 text-teal-600 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-slate-800 mb-2">Switch to the Sub-Inspector track</h2>
            <p className="text-sm text-slate-600 mb-4">The 180-Day Roadmap is built for the AP &amp; TS Police SI exam. Switch your exam to SI to use it.</p>
            <Button onClick={() => navigate("/dashboard")} className="bg-teal-600 hover:bg-teal-700 text-white">Go to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="si-roadmap-page">
      <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="roadmap-back">
        <ArrowLeft className="w-4 h-4" /> Dashboard
      </button>

      {/* Header / progress */}
      <Card className="border-0 shadow-lg overflow-hidden">
        <div className="bg-gradient-to-br from-teal-600 via-emerald-600 to-green-700 p-5 text-white">
          <div className="flex items-center gap-4">
            <ProgressRing pct={pct} />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-white/80 uppercase tracking-wide flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> {state === "TS" ? "Telangana" : "Andhra Pradesh"} SI · 180-Day Roadmap</p>
              <h1 className="text-xl sm:text-2xl font-bold leading-tight">Your Preparation Plan</h1>
              <div className="flex items-center gap-4 mt-2 text-sm">
                <span className="flex items-center gap-1"><Trophy className="w-4 h-4" /> {totalDone}/180 days</span>
                <span className="flex items-center gap-1"><Flame className="w-4 h-4" /> {me?.streak || 0}-day streak</span>
              </div>
            </div>
          </div>

          {/* TS / AP toggle */}
          <div className="mt-4 inline-flex rounded-lg bg-white/15 p-1" data-testid="roadmap-state-toggle">
            {[["TS", "Telangana"], ["AP", "Andhra Pradesh"]].map(([code, name]) => (
              <button
                key={code}
                onClick={() => switchState(code)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${state === code ? "bg-white text-teal-700" : "text-white/90 hover:bg-white/10"}`}
                data-testid={`roadmap-state-${code}`}
              >
                {name}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {!entitled && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-3">
            <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800">Roadmap tracking is an SI PRO feature</p>
              <p className="text-xs text-amber-700">You can browse the full plan below. Upgrade to save your state, track progress and unlock every practice link.</p>
            </div>
            <Button onClick={() => navigate("/upgrade?exam=si")} className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0" data-testid="roadmap-upgrade-btn">Upgrade</Button>
          </CardContent>
        </Card>
      )}

      {/* Today's Plan */}
      {todayData && (
        <Card className="border-2 border-teal-300 bg-gradient-to-br from-teal-50 to-white shadow-md" data-testid="roadmap-today">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-teal-600" />
              <p className="text-sm font-bold text-teal-800">Today · Day {todayDay}</p>
              <Badge variant="outline" className="text-[10px] border-teal-300 text-teal-700 ml-auto">{todayData.phase}</Badge>
            </div>
            <DayCard day={todayDay} dayData={todayData} done={completed.has(todayDay)} onToggle={toggleDay} isToday entitled={entitled} />
          </CardContent>
        </Card>
      )}

      {/* Phase-grouped day list */}
      <div className="space-y-3">
        {phases.map((ph, pi) => {
          const phaseDone = days.filter((d) => d.day >= ph.start && d.day <= ph.end && completed.has(d.day)).length;
          const phasePct = Math.round((phaseDone / ph.count) * 100);
          const isOpen = openPhase === pi;
          return (
            <Card key={ph.name} className="border-slate-200 overflow-hidden" data-testid={`roadmap-phase-${pi}`}>
              <button
                type="button"
                onClick={() => setOpenPhase(isOpen ? -1 : pi)}
                className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-slate-50 transition-colors"
                data-testid={`roadmap-phase-toggle-${pi}`}
              >
                <Target className="w-5 h-5 text-teal-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800">{ph.name}</p>
                  <p className="text-xs text-slate-500">Days {ph.start}–{ph.end} · {phaseDone}/{ph.count} done</p>
                </div>
                <div className="w-16 flex-shrink-0">
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-teal-500" style={{ width: `${phasePct}%` }} />
                  </div>
                  <p className="text-[10px] text-slate-400 text-right mt-0.5">{phasePct}%</p>
                </div>
                <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="px-3 pb-3 space-y-2 bg-slate-50/40">
                  {days.filter((d) => d.day >= ph.start && d.day <= ph.end).map((d) => (
                    <DayCard
                      key={d.day}
                      day={d.day}
                      dayData={d}
                      done={completed.has(d.day)}
                      onToggle={toggleDay}
                      isToday={d.day === todayDay}
                      entitled={entitled}
                    />
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Syllabus notes */}
      {plan?.notes?.length > 0 && (
        <details className="rounded-xl border border-slate-200 bg-white p-4" data-testid="roadmap-notes">
          <summary className="cursor-pointer text-sm font-semibold text-slate-700">Syllabus &amp; weightage notes</summary>
          <div className="mt-3 space-y-2 text-xs text-slate-600">
            {plan.notes.map((n, i) => (
              <div key={i}>
                {n.label && <span className="font-semibold text-slate-800">{n.label}: </span>}
                {n.value}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

export default SIRoadmapPage;
